import React, { ChangeEvent, FormEvent } from 'react';
import { Typography, makeStyles, TextField, Grid, Button, InputLabel, Theme } from '@material-ui/core';
import { useAppState } from '../../../state';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    backgroundColor: '#f8faff',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  title: {
    color: '#1a237e',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    color: '#3949ab',
    marginBottom: theme.spacing(3),
  },
  gutterBottom: {
    marginBottom: '1em',
  },
  inputContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '1.5em 0 3.5em',
    gap: theme.spacing(3),
    '& div:not(:last-child)': {
      marginRight: '1em',
    },
    [theme.breakpoints.down('sm')]: {
      margin: '1.5em 0 2em',
      flexDirection: 'column',
    },
  },
  textFieldContainer: {
    width: '100%',
  },
  inputLabel: {
    color: '#3949ab',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#90caf9',
      },
      '&:hover fieldset': {
        borderColor: '#42a5f5',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1976d2',
      },
    },
  },
  continueButton: {
    backgroundColor: '#1976d2',
    color: 'white',
    padding: '12px 32px',
    borderRadius: '25px',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#1565c0',
    },
    '&.Mui-disabled': {
      backgroundColor: '#bbdefb',
      color: '#white',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}));

interface RoomNameScreenProps {
  name: string;
  roomName: string;
  setName: (name: string) => void;
  setRoomName: (roomName: string) => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

export default function RoomNameScreen({ name, roomName, setName, setRoomName, handleSubmit }: RoomNameScreenProps) {
  const classes = useStyles();
  const { user } = useAppState();

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleRoomNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRoomName(event.target.value);
  };

  const hasUsername = !window.location.search.includes('customIdentity=true') && user?.displayName;

  return (
    <div className={classes.root}>
      <Typography variant="h4" className={classes.title}>
        Let's Connect
      </Typography>
      <Typography variant="body1" className={classes.subtitle}>
        {hasUsername
          ? "Enter the name of a room you'd like to join"
          : "Enter your name and the name of a room you'd like to join"}
      </Typography>
      <form onSubmit={handleSubmit}>
        <div className={classes.inputContainer}>
          {!hasUsername && (
            <div className={classes.textFieldContainer}>
              <InputLabel shrink htmlFor="input-user-name" className={classes.inputLabel}>
                Your Name
              </InputLabel>
              <TextField
                id="input-user-name"
                variant="outlined"
                fullWidth
                size="small"
                value={name}
                onChange={handleNameChange}
                className={classes.textField}
              />
            </div>
          )}
          <div className={classes.textFieldContainer}>
            <InputLabel shrink htmlFor="input-room-name" className={classes.inputLabel}>
              Room Name
            </InputLabel>
            <TextField
              autoCapitalize="false"
              id="input-room-name"
              variant="outlined"
              fullWidth
              size="small"
              value={roomName}
              onChange={handleRoomNameChange}
              className={classes.textField}
            />
          </div>
        </div>
        <Grid container justifyContent="flex-end">
          <Button
            variant="contained"
            type="submit"
            disabled={!name || !roomName}
            className={classes.continueButton}
          >
            Join Room
          </Button>
        </Grid>
      </form>
    </div>
  );
}